import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import moment from "moment";
import { getCSSRS } from "../services/soar";
import { getFieldDate, getFieldName } from "../../scripts/helperScripts";
import { getFormsySubmissionsForClient } from "../../scripts/formScripts";

function CSSRS2({ contactID, clientProgram }) {
  const [expanded, setExpanded] = useState(false);
  const [loadStatus, setLoadStatus] = useState({});
  const [cssrs, setCssrs] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const location = useLocation();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  //Get Care Plans
  useEffect(() => {
    if (loadStatus.loaded || loadStatus.loading || !expanded) {
      return;
    }

    const loadCSSRsRecords = async () => {
      setLoadStatus({ loading: true });
      console.log("Getting C-SSRS Records for: ", contactID);
      const response = await Promise.all([
        getCSSRS(contactID),
        getFormsySubmissionsForClient(
          "2F3EB420-8C88-ED11-AC20-0050F21C653C",
          contactID
        ),
      ]);

      if (!isMountedRef.current) {
        return;
      }

      if (!response) {
        setLoadStatus({ loaded: true });
        return;
      }

      if (response[0]) {
        console.log("C-SSRS: ", response[0]);
        setCssrs(response[0]);
      }

      if (response[1]) {
        console.log("C-SSRS Screeners: ", response[1]);
        setFormSubmissions(response[1].filter((s) => s.Active === "1"));
      }

      setLoadStatus({ loaded: true });
    };

    loadCSSRsRecords();
  }, [expanded, loadStatus]);

  // useEffect(() => {
  //   setLoadStatus({loaded: false});
  // }, [expanded]);

  const add = () => {
    const url =
      process.env.REACT_APP_ORG +
      "/main.aspx?etc=10518&extraqs=%3f_CreateFromId%3d" +
      contactID +
      "%26_CreateFromType%3d2%26etc%3d10518&histKey=457048345&newWindow=true&pagetype=entityrecord&navbar=off";
    window.open(url, "C-SSRS");
  };

  const openCSSRS = (id) => {
    const url = `${process.env.REACT_APP_ORG}/main.aspx?etc=10518&extraqs=formid%3d9185393a-be1f-4630-ad32-0fdce6f080c8&id=${id}&pagetype=entityrecord&navbar=off`;
    window.open(url, "C-SSRS");
  };

  const cssrsMarkup = useMemo(() => {
    if (!loadStatus.loaded) {
      return;
    }

    if (cssrs.length === 0) {
      return <div>No SOAR C-SSRS records for client</div>;
    }

    return cssrs.map((c) => (
      <div
        key={c.sor_cssrssoarid}
        className="p-2 m-2 text-white bg-safy3-1 row"
      >
        <div className="col" title="Created On">
          {getFieldDate(c.sor_date)}
        </div>
        <div className="col" title="Timeframe">
          {getFieldName(c.sor_timeframe)}
        </div>
        <div className="col" title="Risk Level">
          {c.sor_severity}
        </div>
        <i
          title="Edit"
          onClick={() => openCSSRS(c.sor_cssrssoarid)}
          className="fas fa-pen float-end p-1"
        ></i>
      </div>
    ));
  }, [cssrs, loadStatus]);

  const formsMarkup = useMemo(() => {
    if (!loadStatus.loaded) {
      return;
    }

    if (formSubmissions.length === 0) {
      return <div>No C-SSRS Screeners for client</div>;
    }

    const getDateOfAssessment = (submission) => {
      if (!submission.FormResults?.results?.DateOfAssessment?.["@value"]) {
        return;
      }

      return moment(
        submission.FormResults.results.DateOfAssessment["@value"]
      ).format("l");
    };

    return formSubmissions
      .sort((a, b) => (moment(a.CreatedOn).isBefore(b.CreatedOn) ? 1 : -1))
      .map((submission) => (
        <div
          key={submission.SubmissionId}
          className="p-2 m-2 text-white bg-safy3-1 row"
        >
          <div className="col" title="Created On">
            {moment.utc(submission.CreatedOn).local().format("L LT")}
          </div>
          <div className="col" title="Date of Assessment">
            {getDateOfAssessment(submission)}
          </div>
          <div className="col" title="Risk Level">
            {submission.FormResults?.results?.RiskLevel?.["@value"]}
          </div>
          <div className="col" title="Created By">
            {submission.CreatedByName}
          </div>
          <div className="col" title="Signature Status">
            {submission.FinalSigned === "0" ? "Not Final Signed" : "Signed"}
          </div>
          <div className="float-end px-1">
            <Link
              title={`View C-SSRS Screener`}
              className="fas fa-pen text-white"
              to={`/CSSRsScreener${
                submission.VersionIntId !== undefined &&
                submission.VersionIntId !== "1"
                  ? `v${submission.VersionIntId}`
                  : ""
              }/${submission.FormVersionId}/${submission.ContactId}/${
                submission.SubmissionId
              }`}
              state={{
                tab: "cpass",
                previousPath: location.pathname,
              }}
            />
          </div>
        </div>
      ));
  }, [formSubmissions, loadStatus]);

  return (
    <div className="border p-2 mb-4 card-safy">
      <div className="h4">
        <i
          title={expanded ? `Collapse` : `Expand`}
          className={`fas p-1 text-primary fa-chevron-${
            expanded ? "up" : "down"
          }`}
          onClick={() => setExpanded(!expanded)}
        ></i>
        C-SSRS
        <Link
          className="fas fa-plus-circle float-end p-1 text-primary"
          to={`/CSSRsScreener/2F3EB420-8C88-ED11-AC20-0050F21C653C/${contactID}`}
          state={{
            selectedProgram: clientProgram,
          }}
        >
          <i title="Add C-SSRS Screener"></i>
        </Link>
      </div>
      {expanded ? (
        <>
          {loadStatus.loading ? (
            <div>Loading...</div>
          ) : (
            <>
              {formsMarkup}
              {cssrsMarkup}
            </>
          )}
        </>
      ) : null}
    </div>
  );
}

export default CSSRS2;
